import {
  createGeoJSONLayers,
  createStaticLayer,
  layers,
} from "@/configs/map/layers";
import type { GroupShape } from "@/stores/map/types/layerState";
import type { MapLayerActions, MapLayerState } from "@/types/stores/map";
import { PROCESSED_LAYER_PREFIX } from "@/utils/constants/map";
import type { Layer } from "@deck.gl/core";

export function createLayerRetrievalActions(
  set: any,
  get: () => MapLayerState & MapLayerActions
) {
  return {
    getLayersForWorkspace: (workspaceId: string) => {
      const state = get();
      const layers: Layer[] = [];

      layers.push(...get().getStaticLayers());

      layers.push(...get().getGeoJSONLayers(workspaceId));

      const temporaryLayers = state.temporaryLayers[workspaceId] || [];
      layers.push(...temporaryLayers);

      return layers;
    },

    getStaticLayers: () => {
      const state = get();
      const deckLayers: Layer[] = [];

      if (Object.keys(state.staticData).length === 0) {
        return deckLayers;
      }

      Object.entries(state.staticLayers).forEach(([layerId, isVisible]) => {
        if (!isVisible) return;
        const layerConfig = layers[layerId as keyof typeof layers];
        if (!layerConfig) return;

        const data = state.staticData[layerId];
        if (!data) return;

        const staticLayers = createStaticLayer(
          layerId,
          data,
          isVisible,
          layerConfig
        );
        deckLayers.push(...staticLayers);
      });

      return deckLayers;
    },

    getGeoJSONLayers: (workspaceId?: string) => {
      const state = get();
      const layers: Layer[] = [];
      const effectiveWorkspaceId = workspaceId || "";
      const groupState = state.groups[effectiveWorkspaceId];
      const unifiedGroups = state.unifiedGroups[effectiveWorkspaceId];

      const viewState = state.mapInstances[effectiveWorkspaceId]?.viewState;
      const zoom = viewState?.zoom;

      Object.entries(state.geoJSONLayers).forEach(([layerId, layerData]) => {
        if (!layerData || !layerData.data) {
          return;
        }

        // Check unified layer visibility for the data layer
        const dataLayerId = `data:${layerId}`;
        const isDataLayerVisible = state.getLayerVisibilityById(dataLayerId);
        if (!isDataLayerVisible) {
          return;
        }

        const groupColumnName = groupState?.groupColumnName || null;
        const visualizationMode = groupState?.visualizationMode || "points";
        const visualizationLayerType =
          groupState?.visualizationLayerType || null;

        // For brick layers, unified groups are count-based (not column-based)
        // So groupColumnName will be null, but we still have unified groups
        const isBrickLayerMode =
          visualizationMode === "layers" && visualizationLayerType;

        // Determine if we should use chunks based on unified groups, not legacy chunks
        // Unified groups are the source of truth - if they exist, check if they contain chunks
        // If unified groups exist and are all type "group" (not "chunk"), don't use chunks
        // Only use legacy chunks as fallback if unified groups don't exist yet
        // For brick layers, we never use chunks (count-based groups are always type "group")
        const hasUnifiedChunks =
          !isBrickLayerMode &&
          (unifiedGroups?.groups?.some((g) => g.type === "chunk") ?? false);
        const hasUnifiedGroups =
          unifiedGroups?.groups && unifiedGroups.groups.length > 0;
        const legacyChunks = groupState?.chunks;

        // Use chunks only if:
        // 1. Not in brick layer mode, AND
        // 2. (Unified groups exist and contain chunks, OR unified groups don't exist yet and legacy chunks exist)
        // OR
        // 3. In brick layer mode AND unified groups contain chunks (count-based chunks)
        const useChunks = isBrickLayerMode
          ? hasUnifiedChunks // For brick layers, use chunks if unified groups contain them
          : hasUnifiedGroups
          ? hasUnifiedChunks // If unified groups exist, use them as source of truth
          : legacyChunks && legacyChunks.length > 0; // Otherwise, fall back to legacy chunks

        // Build chunks from unified groups if in brick layer mode and chunks exist
        let chunks = useChunks ? legacyChunks : undefined;
        if (isBrickLayerMode && unifiedGroups?.groups && hasUnifiedChunks) {
          // Convert unified groups chunks to legacy chunk format for createGeoJSONLayers
          chunks = unifiedGroups.groups
            .filter((g) => g.type === "chunk")
            .map((chunk) => ({
              value: chunk.id,
              label: chunk.label,
              values: chunk.values, // These are region IDs for brick layers
              color: chunk.color,
              displayName: chunk.displayName,
            }));
        }
        const chunkConfigs = groupState?.chunkConfigs || {};

        // Build visible groups/chunks from unified layer visibility
        // Fall back to groupState.visibleGroups if unified groups haven't been created yet
        const visibleGroups: string[] = [];
        const visibleChunks: string[] = [];

        if (unifiedGroups?.groups && unifiedGroups.groups.length > 0) {
          // Use unified groups as source of truth
          // For unified groups, default to visible unless explicitly set to false
          // This ensures all groups are visible initially when unified groups are created
          unifiedGroups.groups.forEach((group) => {
            const unifiedId =
              group.type === "chunk"
                ? `chunk:${effectiveWorkspaceId}:${group.id}`
                : `group:${effectiveWorkspaceId}:${group.id}`;
            // Check if group is explicitly hidden in layerVisibility
            const isExplicitlyHidden =
              state.layerVisibility[unifiedId] === false;

            // Check if group is in visibleGroups (primary source - set when unified groups are created)
            const isInVisibleGroups =
              groupState?.visibleGroups?.includes(group.id) ?? false;

            // Check if group is explicitly visible in layerVisibility
            const isExplicitlyVisible =
              state.layerVisibility[unifiedId] === true;

            // Group is visible if:
            // 1. Not explicitly hidden, AND
            // 2. (In visibleGroups OR explicitly visible in layerVisibility)
            // Prioritize visibleGroups since it's set synchronously when unified groups are created
            // layerVisibility might have timing issues, so we use visibleGroups as the primary source
            const isVisible =
              !isExplicitlyHidden && (isInVisibleGroups || isExplicitlyVisible);

            if (isVisible) {
              if (group.type === "chunk") {
                visibleChunks.push(group.id);
                // Add all values from the chunk to visibleGroups for filtering
                visibleGroups.push(...group.values);
              } else {
                visibleGroups.push(group.id);
              }
            }
          });
        } else if (
          groupState?.visibleGroups &&
          groupState.visibleGroups.length > 0
        ) {
          // Fallback: use visibleGroups from groupState if unified groups haven't been created yet
          // This ensures dots show up immediately when column is selected, even before unified groups are created
          visibleGroups.push(...groupState.visibleGroups);
        }

        // Build groupConfigs and chunkConfigs from unified state
        // This ensures configurations match exactly what's in the sidebar
        // Priority: unifiedGroups.groupConfigs > group.color/shape > legacy configs
        const groupConfigs: Record<
          string,
          { name?: string; color?: string; shape?: GroupShape }
        > = {};
        const unifiedChunkConfigs: Record<
          string,
          {
            displayName?: string;
            color?: string;
            shape?: GroupShape;
            from?: string;
            to?: string;
          }
        > = {};

        if (unifiedGroups?.groups) {
          unifiedGroups.groups.forEach((group) => {
            const config = unifiedGroups.groupConfigs[group.id];
            if (group.type === "chunk") {
              // For chunks: use config color if it exists and is non-empty, otherwise use group.color
              // Always use group.color as the source of truth to match sidebar display
              const configColor = config?.color?.trim();
              unifiedChunkConfigs[group.id] = {
                displayName: config?.name || group.displayName,
                // Use config color only if it's explicitly set and non-empty
                // Otherwise use group.color which is what the sidebar displays
                color:
                  configColor && configColor !== "" ? configColor : group.color,
                shape: config?.shape || group.shape || "circle",
                from: group.from,
                to: group.to,
              };
            } else {
              // For groups: use config color if it exists and is non-empty, otherwise use group.color
              // Always use group.color as the source of truth to match sidebar display
              const configColor = config?.color?.trim();
              groupConfigs[group.id] = {
                name: config?.name || group.label,
                // Use config color only if it's explicitly set and non-empty
                // Otherwise use group.color which is what the sidebar displays
                color:
                  configColor && configColor !== "" ? configColor : group.color,
                shape: config?.shape || group.shape || "circle",
              };
            }
          });
        }

        // Merge with legacy configs for backward compatibility
        // Unified configs take priority - they're built from unifiedGroups which is the source of truth
        // Start with unified configs (these match the sidebar), then add legacy configs only for items not in unified groups
        const finalGroupConfigs: Record<
          string,
          { name?: string; color?: string; shape?: GroupShape }
        > = {
          ...groupConfigs, // Start with unified configs (source of truth)
        };
        // Add legacy configs only for groups that don't exist in unified groups
        if (groupState?.groupConfigs) {
          Object.entries(groupState.groupConfigs).forEach(
            ([groupId, legacyConfig]) => {
              if (!finalGroupConfigs[groupId]) {
                finalGroupConfigs[groupId] = legacyConfig;
              }
            }
          );
        }

        const finalChunkConfigs: Record<
          string,
          {
            displayName?: string;
            color?: string;
            shape?: GroupShape;
            from?: string;
            to?: string;
          }
        > = {
          ...unifiedChunkConfigs, // Start with unified chunk configs (source of truth)
        };
        // Add legacy chunk configs only for chunks that don't exist in unified groups
        if (chunkConfigs) {
          Object.entries(chunkConfigs).forEach(([chunkId, legacyConfig]) => {
            if (!finalChunkConfigs[chunkId]) {
              finalChunkConfigs[chunkId] = legacyConfig;
            }
          });
        }

        const visibleGroupsKey = visibleGroups.sort().join(",") || "";

        const layerConfig = groupState?.layerConfig;
        const layerRegionConfigs = groupState?.layerRegionConfigs;
        const categoryColorMap = groupState?.categoryColorMap;
        const dataLabelsVisible = state.dataLabelsVisible;

        const geoJSONLayers = createGeoJSONLayers(
          layerId,
          layerData.data,
          true, // Always pass true, visibility is handled by unified system
          finalGroupConfigs,
          visibleGroupsKey,
          groupColumnName,
          visibleGroups,
          zoom,
          undefined, // pointConfigs - not used in unified system
          finalChunkConfigs,
          useChunks ? chunks : undefined, // Only pass chunks if we're using them
          layerConfig,
          visualizationMode,
          visualizationLayerType,
          state.staticData,
          layerRegionConfigs,
          categoryColorMap,
          dataLabelsVisible
        );
        layers.push(...geoJSONLayers);
      });

      return layers;
    },

    getLayerFilter: (workspaceId: string, layerId: string) => {
      const state = get();
      const groupState = state.groups[workspaceId];
      if (!groupState) return null;

      const { groupColumnName } = groupState;

      if (!groupColumnName) {
        return null;
      }

      if (layerId && !layerId.startsWith(PROCESSED_LAYER_PREFIX)) {
        return null;
      }

      return (feature: any) => {
        const currentState = get();
        const currentGroupState = currentState.groups[workspaceId];
        if (!currentGroupState) return true;

        const {
          groupColumnName: currentGroupColumnName,
          visibleGroups: currentVisibleGroups,
        } = currentGroupState;

        if (!currentGroupColumnName) {
          return true;
        }

        if (currentVisibleGroups.length === 0) {
          return true;
        }

        const category = feature.properties?._category;

        if (!category) return true;

        const normalizedCategory = String(category).trim();
        const normalizedVisibleGroups = currentVisibleGroups.map((g) =>
          String(g).trim()
        );

        return normalizedVisibleGroups.includes(normalizedCategory);
      };
    },

    getLayerVisibility: (layerId: string) => {
      const state = get();
      return (
        state.staticLayers[layerId] ||
        state.geoJSONLayers[layerId]?.isVisible ||
        false
      );
    },

    getAllLayers: () => {
      const state = get();
      const allLayers: Record<string, boolean> = { ...state.staticLayers };

      Object.entries(state.geoJSONLayers).forEach(([layerId, layerData]) => {
        allLayers[layerId] = layerData.isVisible;
      });

      return allLayers;
    },

    clearAllLayers: () => {
      set(
        {
          staticLayers: {
            municipalities: false,
            countries: false,
            timezones: false,
          },
          geoJSONLayers: {},
          temporaryLayers: {},
        },
        false,
        "map/clearAllLayers"
      );
    },
  };
}
